import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  usePropertyField,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { useEffect, useState } from 'react';
import { PropertyInfoPanel } from './PropertyInfoPanel';

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

// Address slot group — mirrors the Footer's address pattern so the visit-us
// card becomes a link to the property's Google Place page when set. The
// schema literal references this constant (extractor follows one level).
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at
    // render time inside PropertyInfoPanel. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

/** Tall enough for the full Elise form (calendar + times + contact fields), without the
 *  catalog block's extra empty band under the submit button. */
const ELISE_IFRAME_HEIGHT = 1480;
const ELISE_IFRAME_HEIGHT_MOBILE = 1400;
const ELISE_MOBILE_BREAKPOINT = 768;

function EliseSchedulerEmbed({ url }: { url: string }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < ELISE_MOBILE_BREAKPOINT : false,
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${ELISE_MOBILE_BREAKPOINT - 1}px)`);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const heightPx = `${isMobile ? ELISE_IFRAME_HEIGHT_MOBILE : ELISE_IFRAME_HEIGHT}px`;

  return (
    <div className="cf-elise-embed" style={{ overflow: 'hidden' }}>
      <iframe
        src={url}
        title="Schedule a Tour"
        width="100%"
        height={heightPx}
        scrolling="no"
        style={{
          border: 'none',
          display: 'block',
          width: '100%',
          height: heightPx,
          overflow: 'hidden',
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        loading="lazy"
      />
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const prefix = 'scheduletourform';
  const formBlockPath = `${prefix}.form_block`;

  // Prefer EliseAI's hosted scheduler when the property has a URL; otherwise
  // keep the ApartmentSync / Multihub wizard.
  const eliseSchedulerUrl = usePropertyField('custom_eliseai_tour_scheduler_url');
  const eliseUrl =
    typeof eliseSchedulerUrl === 'string' ? eliseSchedulerUrl.trim() : '';
  const showElise = eliseUrl.length > 0;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-tour-scheduler',
  });

  return (
    <>
      {/* Secondary-color section: form alongside a single column of info cards.
          On mobile the cards stack beneath the form (DOM order + column flex). */}
      <div className={showElise ? 'cf-section cf-section--elise' : 'cf-section'}>
        <div className="cf-container">
          <div className="cf-layout">
            {/* Schedule-a-tour form */}
            <div className={showElise ? 'cf-form-wrap cf-form-wrap--elise' : 'cf-form-wrap'}>
              <EditableToken path={formBlockPath} as="div">
                {showElise ? (
                  <EliseSchedulerEmbed url={eliseUrl} />
                ) : formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="cf-form-empty-card">
                    <p className="cf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>

            {/* Visit Us / Call Us / Office Hours — stacked in a single column */}
            <div className="cf-info-col">
              <PropertyInfoPanel prefix={prefix} addressDefinition={addressLinkDef} />
            </div>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key.
          The live EditableTokens in PropertyInfoPanel use dynamic paths from prefix. */}
      <EditableToken path="scheduletourform.form_block"   as="div"  className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.visit_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableSlotGroup
        groupPath="scheduletourform.address"
        definition={addressLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableToken path="scheduletourform.call_label"   as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.phone"        as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>
      <EditableToken path="scheduletourform.hours_label"  as="span" className="hidden" style={{ display: 'none' }}>{''}</EditableToken>

      <style>{`
        /* ── Section: solid secondary-color background ── */
        .cf-section {
          background: var(--vibe-color-secondary, #3E441D);
          padding: 2rem 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-section { padding: 4rem 2rem; }
        }
        /* Elise iframe is already tall — tighten the band under the card before the map. */
        .cf-section--elise {
          padding-bottom: 1.25rem;
        }
        @media (min-width: 768px) {
          .cf-section--elise { padding-bottom: 2rem; }
        }
        .cf-container {
          max-width: 72rem;
          margin: 0 auto;
        }

        /* Mobile: single column — form first, info cards beneath.
           Wider screens: form beside a single column of info cards. */
        .cf-layout {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }
        @media (min-width: 768px) {
          .cf-layout {
            flex-direction: row;
            align-items: flex-start;
            gap: 2rem;
          }
        }

        .cf-form-wrap {
          width: 100%;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }
        @media (min-width: 768px) {
          .cf-form-wrap { flex: 1 1 60%; min-width: 0; }
        }
        .cf-form-wrap--elise {
          background: #fff;
        }
        /* Uppercase the block's wizard heading */
        .cf-form-wrap h3 {
          text-transform: uppercase;
        }
        .cf-form-empty-card {
          background: white;
          border-radius: var(--vibe-radius-lg, 12px);
          padding: 2rem;
        }
        .cf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        /* Info column — sits beside the form on wider screens */
        .cf-info-col {
          width: 100%;
        }
        @media (min-width: 768px) {
          .cf-info-col { flex: 1 1 40%; max-width: 24rem; }
        }

        /* Stack the Visit Us / Call Us / Office Hours cards vertically.
           Scoped to this layout so the shared PropertyInfoPanel keeps its
           default (horizontal) styling elsewhere. */
        .cf-info-col .cf-info-cards {
          flex-direction: column;
          flex-wrap: nowrap;
          gap: 1rem;
        }
        .cf-info-col .cf-card {
          flex: 0 0 auto;
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
      description:
        'Used when the property has no EliseAI tour scheduler URL. When custom_eliseai_tour_scheduler_url is set, that embed is shown instead.',
    },
    'scheduletourform.visit_label': {
      label: 'Visit Us Label',
      type: 'text',
      defaultValue: 'Visit Us',
    },
    'scheduletourform.call_label': {
      label: 'Call Us Label',
      type: 'text',
      defaultValue: 'Call Us',
    },
    'scheduletourform.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '',
    },
    'scheduletourform.hours_label': {
      label: 'Office Hours Label',
      type: 'text',
      defaultValue: 'Office Hours',
    },
  },
  slotGroups: {
    'scheduletourform.address': addressLinkDef,
  },
  collectionBindings: {
    'scheduletourform.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
